import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const PERIODS = Array.from({ length: 8 }, (_, index) => index + 1);
const CUSTOM_CLASS = "__custom_class__";
const blankEquipment = () => ({ item: "", qty: "1", units: "", notes: "", per_class: false });

const bookingForm = (booking) => ({
  title: booking.practical.name || "",
  subject: booking.practical.subject || "",
  class_name: booking.lesson.class_name || "",
  class_id: booking.lesson.class_id || "",
  year_group: booking.lesson.year_group ?? "",
  pupils: booking.lesson.pupils ?? "",
  lab_id: booking.lesson.lab_id || "",
  date: booking.lesson.date || "",
  period: String(booking.lesson.period || 1),
  double_period: Boolean(
    booking.lesson.double_period || (booking.lesson.periods || []).length === 2,
  ),
  sets: String(booking.sets || 1),
  notes: booking.notes || "",
  extra_equipment: booking.extra_equipment?.length
    ? booking.extra_equipment.map((item) => ({
        item: item.item || "",
        qty: String(item.qty_per_group || item.qty_fixed || 1),
        units: item.units || "",
        notes: item.notes || "",
        per_class: Boolean(item.per_class),
      }))
    : [blankEquipment()],
});

export default function BookingEditDialog({ booking, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(() => bookingForm(booking));
  const [labs, setLabs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(bookingForm(booking));
    Promise.all([api.get("/rooms"), api.get("/timetable/meta")])
      .then(([rooms, timetable]) => {
        setLabs(rooms.data.filter((room) => (room.kind || "lab") === "lab"));
        setClasses(timetable.data.classes || []);
      })
      .catch(() => {
        setLabs([]);
        setClasses([]);
      });
  }, [booking, open]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const choosePeriod = (period) => {
    setForm((current) => ({
      ...current,
      period,
      double_period: Number(period) >= PERIODS.length ? false : current.double_period,
    }));
  };
  const chooseClass = (classId) => {
    if (classId === CUSTOM_CLASS) {
      update("class_id", "");
      return;
    }
    const selectedClass = classes.find((schoolClass) => schoolClass.id === classId);
    if (!selectedClass) {
      return;
    }
    setForm((current) => ({
      ...current,
      class_id: selectedClass.id,
      class_name: selectedClass.name,
      year_group: selectedClass.year_group ?? "",
      pupils: selectedClass.size ?? "",
    }));
  };
  const updateEquipment = (index, key, value) => {
    setForm((current) => ({
      ...current,
      extra_equipment: current.extra_equipment.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    }));
  };
  const addEquipment = () => {
    setForm((current) => ({
      ...current,
      extra_equipment: [...current.extra_equipment, blankEquipment()],
    }));
  };
  const removeEquipment = (index) => {
    setForm((current) => ({
      ...current,
      extra_equipment: current.extra_equipment.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const save = async () => {
    const className = form.class_name.trim();
    if (!form.title.trim() || !className || !form.date) {
      toast.error("Add a practical title, class name and date before saving");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/bookings/${booking.id}`, {
        title: form.title.trim(),
        subject: form.subject,
        class_id: form.class_id || null,
        class_name: className,
        year_group: form.year_group ? Number(form.year_group) : null,
        pupils: form.pupils ? Number(form.pupils) : null,
        lab_id: form.lab_id || null,
        date: form.date,
        period: Number(form.period),
        double_period: form.double_period,
        sets: Number(form.sets),
        notes: form.notes,
        extra_equipment: form.extra_equipment
          .filter((item) => item.item.trim())
          .map((item) => ({
            item: item.item.trim(),
            qty: Number(item.qty) || 1,
            units: item.units.trim(),
            notes: item.notes.trim(),
            per_class: item.per_class,
          })),
      });
      toast.success("Booking updated");
      onSaved(data);
      onOpenChange(false);
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        data-testid="booking-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit booking {booking.reference}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="booking-edit-title">Practical title</Label>
            <Input
              id="booking-edit-title"
              value={form.title}
              onChange={(event) => update("title", event.target.value)}
              data-testid="booking-edit-title-input"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Subject</Label>
              <Select
                value={form.subject || "none"}
                onValueChange={(value) => update("subject", value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="booking-edit-subject-select">
                  <SelectValue placeholder="Choose subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No subject</SelectItem>
                  {['Biology', 'Chemistry', 'Physics', 'Other'].map((subject) => (
                    <SelectItem key={subject} value={subject}>
                      {subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Class</Label>
              <Select
                value={classes.some((schoolClass) => schoolClass.id === form.class_id)
                  ? form.class_id
                  : CUSTOM_CLASS}
                onValueChange={chooseClass}
              >
                <SelectTrigger data-testid="booking-edit-class-select">
                  <SelectValue placeholder="Choose a saved class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_CLASS}>Type another class</SelectItem>
                  {classes.map((schoolClass) => (
                    <SelectItem
                      key={schoolClass.id}
                      value={schoolClass.id}
                      data-testid={`booking-edit-class-option-${schoolClass.id}`}
                    >
                      {schoolClass.name}
                      {schoolClass.size ? ` · ${schoolClass.size} pupils` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-edit-class">Class name</Label>
              <Input
                id="booking-edit-class"
                value={form.class_name}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    class_id: "",
                    class_name: event.target.value,
                  }));
                }}
                data-testid="booking-edit-class-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-edit-year">Year group</Label>
              <Input
                id="booking-edit-year"
                min="1"
                max="13"
                type="number"
                value={form.year_group}
                onChange={(event) => update("year_group", event.target.value)}
                data-testid="booking-edit-year-input"
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="booking-edit-pupils">Pupils</Label>
              <Input
                id="booking-edit-pupils"
                min="1"
                max="500"
                type="number"
                value={form.pupils}
                onChange={(event) => update("pupils", event.target.value)}
                data-testid="booking-edit-pupils-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-edit-date">Date</Label>
              <Input
                id="booking-edit-date"
                type="date"
                value={form.date}
                onChange={(event) => update("date", event.target.value)}
                data-testid="booking-edit-date-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Period</Label>
              <Select value={form.period} onValueChange={choosePeriod}>
                <SelectTrigger data-testid="booking-edit-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIODS.map((period) => (
                    <SelectItem key={period} value={String(period)}>
                      Period {period}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="mt-2 flex items-center gap-2 text-xs">
                <Checkbox
                  checked={form.double_period}
                  disabled={Number(form.period) >= PERIODS.length}
                  onCheckedChange={(value) => update("double_period", Boolean(value))}
                  data-testid="booking-edit-double-period"
                />
                Double period — required in periods {form.period} and {Number(form.period) + 1}
              </label>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Laboratory</Label>
              <Select
                value={form.lab_id || "none"}
                onValueChange={(value) => update("lab_id", value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="booking-edit-lab-select">
                  <SelectValue placeholder="No lab" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No lab</SelectItem>
                  {labs.map((lab) => (
                    <SelectItem key={lab.id} value={lab.id}>
                      {lab.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-edit-sets">Equipment sets</Label>
              <Input
                id="booking-edit-sets"
                min="1"
                max="60"
                type="number"
                value={form.sets}
                onChange={(event) => update("sets", event.target.value)}
                data-testid="booking-edit-sets-input"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="booking-edit-notes">Notes for the technician</Label>
            <Textarea
              id="booking-edit-notes"
              rows={3}
              value={form.notes}
              onChange={(event) => update("notes", event.target.value)}
              data-testid="booking-edit-notes-input"
            />
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <Label>Additional equipment</Label>
                <p className="text-xs text-muted-foreground">
                  Add items that were missed when the booking was made.
                </p>
              </div>
              <Button
                onClick={addEquipment}
                size="sm"
                type="button"
                variant="outline"
                data-testid="booking-edit-add-equipment-button"
              >
                <Plus className="mr-1 h-4 w-4" />
                Add equipment
              </Button>
            </div>
            <div className="space-y-2">
              {form.extra_equipment.map((item, index) => (
                <div
                  key={`equipment-${index}`}
                  className={[
                    "grid gap-2 rounded-lg border p-3",
                    "sm:grid-cols-[1.4fr_5rem_7rem_1fr_2.5rem] sm:items-center",
                  ].join(" ")}
                  data-testid={`booking-edit-equipment-row-${index}`}
                >
                  <Input
                    placeholder="Equipment item"
                    value={item.item}
                    onChange={(event) => updateEquipment(index, "item", event.target.value)}
                    data-testid={`booking-edit-equipment-item-${index}`}
                  />
                  <Input
                    min="0.01"
                    placeholder="Qty"
                    step="0.01"
                    type="number"
                    value={item.qty}
                    onChange={(event) => updateEquipment(index, "qty", event.target.value)}
                    data-testid={`booking-edit-equipment-qty-${index}`}
                  />
                  <Input
                    placeholder="Units"
                    value={item.units}
                    onChange={(event) => updateEquipment(index, "units", event.target.value)}
                    data-testid={`booking-edit-equipment-units-${index}`}
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={item.per_class}
                      onCheckedChange={(value) =>
                        updateEquipment(index, "per_class", Boolean(value))
                      }
                      data-testid={`booking-edit-equipment-per-class-${index}`}
                    />
                    <span className="text-xs text-muted-foreground">Per class</span>
                  </div>
                  <Button
                    aria-label={`Remove equipment row ${index + 1}`}
                    disabled={form.extra_equipment.length === 1}
                    onClick={() => removeEquipment(index)}
                    size="icon"
                    title="Remove equipment"
                    type="button"
                    variant="ghost"
                    data-testid={`booking-edit-equipment-remove-${index}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            disabled={saving}
            onClick={() => onOpenChange(false)}
            type="button"
            variant="outline"
            data-testid="booking-edit-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={save}
            data-testid="booking-edit-save-button"
          >
            {saving ? "Saving…" : "Save booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}